import { PerfilDeOrganizacao } from "@/types/gerais"

export type InformacoesDoUsuarioLogado = {
  id: string
  perfilDeOrganizacao?: PerfilDeOrganizacao | ""
  permissoes?: string[]
  perfilDePermissao?: {
    id: string
    nome?: string | null
    ehRh?: boolean | null
    permissoes: string[]
  } | null
  nome?: string
  idEmpresa?: string
}

type ProfissionalDoUsuario = {
  id: string
  nome: string
  ativo: boolean
}

export default class SessaoServico {
  static readonly chaveToken = "_5azn3gn"
  static readonly chaveTokenRenovacao = "_fwla2rs"
  static readonly chaveTipo = "_t2o6x2o"
  static readonly chaveProfissionalDoUsuario = "_p2r0o2f"
  static readonly chaveUrlRenovacaoTokenKeycloak = "_keycloak_refresh_url"
  static readonly chaveUrlLogoutKeycloak = "_keycloak_logout_url"
  static readonly chaveRealmKeycloak = "_keycloak_realm"
  static readonly chavePermissoes = "_p2e2r2m"

  private static informacoesDoUsuarioLogado: InformacoesDoUsuarioLogado = {
    id: "",
    perfilDeOrganizacao: "",
  }

  private static carregamentoDoUsuarioLogado: Promise<boolean> | null = null
  private static permissionamentoGranular = false


  private static seNuloEntaoStringVazia(valor: string | null): string {
    return valor || ""
  }

  static set tokenRenovacao(valor: string | null) {
    localStorage.setItem(
      this.chaveTokenRenovacao,
      this.seNuloEntaoStringVazia(valor)
    )
  }

  static get tokenRenovacao(): string | null {
    return localStorage.getItem(this.chaveTokenRenovacao)
  }

  static set token(valor: string | null) {
    localStorage.setItem(this.chaveToken, this.seNuloEntaoStringVazia(valor))
  }

  static get token(): string | null {
    return localStorage.getItem(this.chaveToken)
  }

  static set tipo(valor: string | null) {
    localStorage.setItem(this.chaveTipo, this.seNuloEntaoStringVazia(valor))
  }

  static get tipo(): string | null {
    return localStorage.getItem(this.chaveTipo)
  }

  static set urlRenovacaoTokenKeycloak(valor: string | null) {
    localStorage.setItem(
      this.chaveUrlRenovacaoTokenKeycloak,
      this.seNuloEntaoStringVazia(valor)
    )
  }

  static get urlRenovacaoTokenKeycloak(): string {
    return localStorage.getItem(this.chaveUrlRenovacaoTokenKeycloak) || ""
  }

  static set urlLogoutKeycloak(valor: string | null) {
    localStorage.setItem(
      this.chaveUrlLogoutKeycloak,
      this.seNuloEntaoStringVazia(valor)
    )
  }

  static get urlLogoutKeycloak(): string {
    return localStorage.getItem(this.chaveUrlLogoutKeycloak) || ""
  }

  static set realmKeycloak(valor: string | null) {
    localStorage.setItem(
      this.chaveRealmKeycloak,
      this.seNuloEntaoStringVazia(valor)
    )
  }

  static get realmKeycloak(): string {
    return localStorage.getItem(this.chaveRealmKeycloak) || ""
  }

  static get ehSessaoKeycloak(): boolean {
    return !!this.urlRenovacaoTokenKeycloak
  }

  static get autenticado(): boolean {
    return !!this.token
  }

  static get usuarioPossuiProfissionalAtivo(): boolean {
    const profissionalDoUsuario =
      this.coletarInformacoesDeProfissionalDoUsuario()
    return !!profissionalDoUsuario && profissionalDoUsuario.ativo
  }

  static set profissionalDoUsuario(valor: string | null) {
    localStorage.setItem(
      this.chaveProfissionalDoUsuario,
      this.seNuloEntaoStringVazia(valor)
    )
  }

  static limparInformacoesDaSessao(): void {
    this.token = ""
    this.tokenRenovacao = ""
    this.tipo = ""
    this.profissionalDoUsuario = ""
    this.urlRenovacaoTokenKeycloak = ""
    this.urlLogoutKeycloak = ""
    this.realmKeycloak = ""
    localStorage.removeItem(this.chavePermissoes)
    this.carregamentoDoUsuarioLogado = null
    this.limparInformacoesDoUsuarioLogado()
  }

  static iniciarCarregamentoDoUsuarioLogado(
    carregarUsuarioLogado: () => Promise<InformacoesDoUsuarioLogado>
  ): void {
    if (
      !this.autenticado ||
      this.informacoesDoUsuarioLogado.id ||
      this.carregamentoDoUsuarioLogado
    ) {
      return
    }

    this.carregamentoDoUsuarioLogado = carregarUsuarioLogado()
      .then((usuarioLogado) => {
        this.definirInformacoesDoUsuarioLogado(usuarioLogado)
        return true
      })
      .catch(() => {
        this.limparInformacoesDaSessao()
        return false
      })
  }

  static async aguardarCarregamentoDoUsuarioLogado(): Promise<boolean> {
    if (this.informacoesDoUsuarioLogado.id) {
      return true
    }

    return this.carregamentoDoUsuarioLogado
      ? this.carregamentoDoUsuarioLogado
      : false
  }

  static salvarPermissoes(permissoes: string[] = []): void {
    localStorage.setItem(this.chavePermissoes, JSON.stringify(permissoes))
  }

  static get usaPermissionamentoGranular(): boolean {
    return this.permissionamentoGranular
  }

  static coletarPermissoes(): string[] {
    const raw = localStorage.getItem(this.chavePermissoes)
    const angularRoles = localStorage.getItem("angular_session_roles")
    const lista: string[] = []
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) lista.push(...parsed)
      } catch {}
    }
    if (angularRoles) {
      try {
        const parsed = JSON.parse(angularRoles)
        if (Array.isArray(parsed)) lista.push(...parsed)
      } catch {}
    }
    return Array.from(new Set(lista))
  }

  static definirInformacoesDoUsuarioLogado(
    usuario: InformacoesDoUsuarioLogado
  ): void {
    this.permissionamentoGranular = Object.prototype.hasOwnProperty.call(
      usuario,
      "perfilDePermissao"
    )
    const permissoes =
      usuario.perfilDePermissao?.permissoes ?? usuario.permissoes ?? []
    this.informacoesDoUsuarioLogado = { ...usuario }
    if (this.permissionamentoGranular || usuario.permissoes !== undefined) {
      this.informacoesDoUsuarioLogado.permissoes = permissoes
    }
    this.salvarPermissoes(permissoes)
  }

  static limparInformacoesDoUsuarioLogado(): void {
    this.permissionamentoGranular = false
    this.informacoesDoUsuarioLogado = {
      id: "",
      perfilDeOrganizacao: "",
    }
  }

  static coletarInformacoesDoUsuarioLogado(): InformacoesDoUsuarioLogado {
    return this.informacoesDoUsuarioLogado
  }

  static salvarInformacoesDeProfissionalDoUsuario(
    profissional: ProfissionalDoUsuario | null = null
  ): void {
    const profissionalCodificado = Buffer.from(
      JSON.stringify(profissional)
    ).toString("base64")
    localStorage.setItem(
      this.chaveProfissionalDoUsuario,
      profissionalCodificado
    )
  }

  static coletarInformacoesDeProfissionalDoUsuario(): ProfissionalDoUsuario {
    const profissionalCodificado = localStorage.getItem(
      this.chaveProfissionalDoUsuario
    )
    const objetoVazio = {
      id: "",
      nome: "",
      ativo: false,
    }
    if (profissionalCodificado) {
      const profissionalDecodificado = Buffer.from(
        profissionalCodificado,
        "base64"
      ).toString("utf-8")
      return JSON.parse(profissionalDecodificado) || objetoVazio
    }
    return objetoVazio
  }
}
